// Sandbox 实例日志视图 —— 消费者页（pages/sandbox.jsx）与 Console「Sandbox 实例」tab 共用。
//
// 三段内容对应后端一次详情请求返回的三个数组：
//   operations    最近的操作记录（控制面 CREATE/SUSPEND/RESUME/TERMINATE/EXTENSION +
//                 数据面 EXEC/FILE_*/PORT_OPEN；数据面才带命令原文与 stdout/stderr）
//   events        状态时间线（previousState → currentState + reason）
//   usageSegments 计费段（按状态分段，OPEN 段还没结算所以 endsAt/amount 为 null）
//
// 这里只做展示。取数、轮询、动作按钮都归调用方 —— 两个页面的鉴权方式不同
// （消费者页只有 gateway 的 xapi-key，Console 还有 JWT 只读接口），组件不该关心。
import React from 'react';
import { useT } from '../i18n.jsx';

// -------- Gateway 直连（写操作唯一入口，认 xapi-key）--------
export const sandboxBaseForHostname = (hostname = '') => {
  if (/^test\.xapi\.to$/i.test(hostname)) {
    return 'https://sandbox.test.xapi.to';
  }
  if (/^(?:www\.)?xapi\.to$/i.test(hostname)) {
    return 'https://sandbox.xapi.to';
  }
  return '/sandbox-api';
};

const sandboxBaseFromLocation = () => (
  typeof window === 'undefined'
    ? '/sandbox-api'
    : sandboxBaseForHostname(window.location.hostname)
);

export const SANDBOX_BASE = String(
  import.meta.env.VITE_SANDBOX_API_BASE || sandboxBaseFromLocation(),
).replace(/\/$/, '');

// 明文 key 只放 sessionStorage：关标签页即失效。Console 与消费者页共用同一个
// storage key，任一页面连过一次，另一页就不用再粘一遍。
const KEY_STORAGE = 'xapi.sandbox.session-key';
export const sandboxKeyStore = {
  get: () => { try { return sessionStorage.getItem(KEY_STORAGE) || ''; } catch { return ''; } },
  set: (value) => { try { sessionStorage.setItem(KEY_STORAGE, value); } catch { /* private mode */ } },
  clear: () => { try { sessionStorage.removeItem(KEY_STORAGE); } catch { /* private mode */ } },
};

const errorMessage = (body, status) => {
  const raw = body?.message ?? body?.error ?? `Sandbox Gateway 请求失败（HTTP ${status}）`;
  return Array.isArray(raw) ? raw.join('；') : String(raw);
};

export async function sandboxRequest(path, { apiKey, ...init } = {}) {
  const headers = new Headers(init.headers || {});
  headers.set('accept', 'application/json');
  if (apiKey) headers.set('authorization', `Bearer ${apiKey}`);
  if (init.body) headers.set('content-type', 'application/json');
  const response = await fetch(`${SANDBOX_BASE}${path}`, { ...init, headers });
  const text = await response.text();
  let body = null;
  const contentType = response.headers.get('content-type') || '';
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!response.ok) throw new Error(errorMessage(body, response.status));
  if (text && !contentType.toLowerCase().includes('application/json')) {
    throw new Error(
      `Sandbox Gateway 返回了非 JSON 响应（${contentType || '未知内容类型'}）。请检查 VITE_SANDBOX_API_BASE 或 /sandbox-api 代理配置。`,
    );
  }
  return body;
}

// -------- 格式化 --------
export const sandboxDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

export const sandboxMoney = (value, digits = 6) => {
  if (value == null || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  if (parsed === 0) return '$0';
  return `$${parsed.toFixed(digits).replace(/0+$/, '').replace(/\.$/, '')}`;
};

export const sandboxDurationMs = (ms) => {
  if (ms == null || !Number.isFinite(Number(ms))) return '—';
  const value = Number(ms);
  if (value < 1000) return `${Math.round(value)}ms`;
  if (value < 60_000) return `${(value / 1000).toFixed(value < 10_000 ? 2 : 1)}s`;
  return `${Math.floor(value / 60_000)}m${Math.round((value % 60_000) / 1000)}s`;
};

export const sandboxDurationSeconds = (seconds) => {
  if (seconds == null || seconds === '') return '—';
  const value = Number(seconds);
  if (!Number.isFinite(value)) return '—';
  if (value < 60) return `${value.toFixed(value < 10 ? 2 : 1).replace(/\.?0+$/, '')}s`;
  if (value < 3600) return `${Math.floor(value / 60)}m${Math.round(value % 60)}s`;
  return `${Math.floor(value / 3600)}h${Math.round((value % 3600) / 60)}m`;
};

// -------- 类型 / 状态元数据 --------
// 数据面 = 用户实际在沙箱里跑的东西（命令、文件、端口），控制面 = 生命周期动作。
// 两者在列表里用不同色徽标区分：一眼能分出「谁在用」和「谁在改状态」。
export const SANDBOX_DATA_PLANE_TYPES = ['EXEC', 'FILE_READ', 'FILE_WRITE', 'FILE_LIST', 'PORT_OPEN'];
export const SANDBOX_CONTROL_PLANE_TYPES = ['CREATE', 'SUSPEND', 'RESUME', 'TERMINATE', 'EXTENSION', 'SNAPSHOT'];
export const SANDBOX_OPERATION_TYPES = [...SANDBOX_CONTROL_PLANE_TYPES, ...SANDBOX_DATA_PLANE_TYPES];

export const isSandboxDataPlaneOp = (type) => SANDBOX_DATA_PLANE_TYPES.includes(type);

const STATUS_COLOR = {
  SUCCEEDED: 'var(--ok)',
  FAILED: 'var(--err)',
  RUNNING: 'var(--warn)',
  PENDING: 'var(--fg-4)',
  CANCELLED: 'var(--fg-4)',
};
export const sandboxStatusColor = (status) => STATUS_COLOR[status] || 'var(--fg-3)';

export const SandboxOpTypeBadge = ({ type }) => {
  const dataPlane = isSandboxDataPlaneOp(type);
  return (
    <span
      className="mono sbx-op-type"
      style={{
        color: dataPlane ? '#8eb0ff' : 'var(--fg-2)',
        borderColor: dataPlane ? 'rgba(142,176,255,0.35)' : 'var(--line)',
        background: dataPlane ? 'rgba(142,176,255,0.08)' : 'transparent',
      }}
    >
      {type || '—'}
    </span>
  );
};

const OutputBlock = ({ label, text, tone }) => (
  <div className="sbx-out">
    <div className="micro sbx-out-label">{label}</div>
    <pre className={`sbx-out-pre${tone === 'err' ? ' err' : ''}`}>{text}</pre>
  </div>
);

// 一条操作的「正文」：命令原文 + 输出 + 失败原因。列表行和 Console 的展开行共用。
export const SandboxOperationBody = ({ op }) => {
  const { t } = useT();
  const command = op?.input?.command;
  // EXEC 之外的数据面调用（文件、端口）没有命令，但 input 里有路径/端口，一样值得展示。
  const otherInput = !command && op?.input && Object.keys(op.input).length
    ? JSON.stringify(op.input)
    : '';
  const hasBody = command || otherInput || op?.stdoutPreview || op?.stderrPreview || op?.error;
  if (!hasBody) return null;
  return (
    <div className="sbx-op-body">
      {command && <OutputBlock label={t('sandbox.log.command')} text={command} />}
      {otherInput && <OutputBlock label={t('sandbox.log.input')} text={otherInput} />}
      {op.stdoutPreview && <OutputBlock label={t('sandbox.log.stdout')} text={op.stdoutPreview} />}
      {op.stderrPreview && <OutputBlock label={t('sandbox.log.stderr')} text={op.stderrPreview} tone="err" />}
      {op.outputTruncated && <div className="sbx-op-trunc">{t('sandbox.log.truncated')}</div>}
      {op.error && (
        <div className="sbx-op-error">
          <span className="micro">{t('sandbox.log.error')}</span>
          <span>{op.error}</span>
        </div>
      )}
    </div>
  );
};

export const SandboxOperationRow = ({ op }) => {
  const { t } = useT();
  return (
    <li className="sbx-op">
      <div className="sbx-op-head">
        <SandboxOpTypeBadge type={op.type} />
        <span className="mono sbx-op-status" style={{ color: sandboxStatusColor(op.status) }}>
          {op.status || '—'}
        </span>
        {op.exitCode != null && (
          <span className="mono sbx-op-meta" style={{ color: op.exitCode === 0 ? 'var(--fg-4)' : 'var(--err)' }}>
            {t('sandbox.log.exit')} {op.exitCode}
          </span>
        )}
        {op.durationMs != null && (
          <span className="mono sbx-op-meta">{sandboxDurationMs(op.durationMs)}</span>
        )}
        <span className="grow" />
        <span className="mono sbx-op-meta">{sandboxDateTime(op.requestedAt)}</span>
      </div>
      <SandboxOperationBody op={op} />
    </li>
  );
};

const EmptyLine = ({ children }) => <div className="sbx-log-empty">{children}</div>;

export const SandboxOperationsList = ({ items }) => {
  const { t } = useT();
  if (!items?.length) return <EmptyLine>{t('sandbox.log.empty.operations')}</EmptyLine>;
  return (
    <ul className="sbx-op-list">
      {items.map((op, index) => <SandboxOperationRow key={op.id || index} op={op} />)}
    </ul>
  );
};

export const SandboxEventsTimeline = ({ items }) => {
  const { t } = useT();
  if (!items?.length) return <EmptyLine>{t('sandbox.log.empty.events')}</EmptyLine>;
  return (
    <ul className="sbx-event-list">
      {items.map((event, index) => (
        <li className="sbx-event" key={event.id || `${event.observedAt}:${index}`}>
          <span className="sbx-event-dot" />
          <div className="sbx-event-body">
            <div className="mono sbx-event-states">
              <span style={{ color: 'var(--fg-4)' }}>{event.previousState || '—'}</span>
              <span style={{ color: 'var(--fg-4)' }}> → </span>
              <span style={{ color: 'var(--fg-1)' }}>{event.currentState || '—'}</span>
            </div>
            <div className="sbx-event-meta">
              {sandboxDateTime(event.observedAt)}
              {event.reason ? ` · ${t('sandbox.log.reason')} ${event.reason}` : ''}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const SandboxUsageSegments = ({ items, totalCost }) => {
  const { t } = useT();
  if (!items?.length) return <EmptyLine>{t('sandbox.log.empty.segments')}</EmptyLine>;
  // 合计只累加已结算金额；OPEN 段还在跑，金额为 null。
  const sum = items.reduce((acc, seg) => acc + (Number(seg.amount) || 0), 0);
  return (
    <div className="sbx-seg-wrap">
      <table className="sbx-seg-table">
        <thead>
          <tr>
            <th>{t('sandbox.log.col.state')}</th>
            <th>{t('sandbox.log.col.window')}</th>
            <th style={{ textAlign: 'right' }}>{t('sandbox.log.col.duration')}</th>
            <th style={{ textAlign: 'right' }}>{t('sandbox.log.col.amount')}</th>
            <th style={{ textAlign: 'right' }}>{t('sandbox.log.col.settle')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((seg, index) => (
            <tr key={seg.id || `${seg.startsAt}:${index}`}>
              <td className="mono">{seg.state || '—'}</td>
              <td className="mono sbx-seg-window">
                {sandboxDateTime(seg.startsAt)}
                <span style={{ color: 'var(--fg-4)' }}> → </span>
                {seg.endsAt ? sandboxDateTime(seg.endsAt) : t('sandbox.log.segOpen')}
              </td>
              <td className="mono" style={{ textAlign: 'right' }}>{sandboxDurationSeconds(seg.durationSeconds)}</td>
              <td className="mono" style={{ textAlign: 'right' }}>{sandboxMoney(seg.amount, 8)}</td>
              <td
                className="mono"
                style={{ textAlign: 'right', color: seg.status === 'SETTLED' ? 'var(--ok)' : 'var(--warn)' }}
              >
                {seg.status || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sbx-seg-total">
        <span>{t('sandbox.log.segTotal')}</span>
        <b className="mono">{sandboxMoney(sum, 8)}</b>
        {totalCost != null && (
          <span className="sbx-seg-total-hint">
            {t('sandbox.log.totalCost')} {sandboxMoney(totalCost, 8)}
          </span>
        )}
      </div>
    </div>
  );
};

export const SandboxBillingPeriods = ({ items, totalCost }) => {
  const { t } = useT();
  if (!items?.length) return <EmptyLine>{t('sandbox.log.empty.periods')}</EmptyLine>;
  const sum = items.reduce((acc, period) => acc + (Number(period.amount) || 0), 0);
  return (
    <div className="sbx-seg-wrap">
      <table className="sbx-seg-table">
        <thead>
          <tr>
            <th>{t('sandbox.log.col.kind')}</th>
            <th>{t('sandbox.log.col.window')}</th>
            <th style={{ textAlign: 'right' }}>{t('sandbox.log.col.amount')}</th>
            <th style={{ textAlign: 'right' }}>{t('sandbox.log.col.settle')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((period, index) => (
            <tr key={period.id || `${period.startsAt}:${index}`}>
              <td className="mono">{period.kind || '—'}</td>
              <td className="mono sbx-seg-window">
                {sandboxDateTime(period.startsAt)}
                <span style={{ color: 'var(--fg-4)' }}> → </span>
                {period.endedAt ? sandboxDateTime(period.endedAt) : t('sandbox.log.segOpen')}
              </td>
              <td className="mono" style={{ textAlign: 'right' }}>{sandboxMoney(period.amount, 8)}</td>
              <td className="mono" style={{ textAlign: 'right', color: period.status === 'SETTLED' ? 'var(--ok)' : 'var(--warn)' }}>
                {period.status || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sbx-seg-total">
        <span>{t('sandbox.log.periodTotal')}</span>
        <b className="mono">{sandboxMoney(sum, 8)}</b>
        {totalCost != null && <span className="sbx-seg-total-hint">{t('sandbox.log.totalCost')} {sandboxMoney(totalCost, 8)}</span>}
      </div>
    </div>
  );
};

// 三段合一的面板。detail 为 null 时展示加载中 / 错误占位。
export const SandboxLogPanel = ({
  detail,
  error,
  defaultTab = 'operations',
  onLoadMore,
  loadingMore = false,
}) => {
  const { t } = useT();
  const [tab, setTab] = React.useState(defaultTab);
  const tabs = [
    ['operations', t('sandbox.log.tab.operations'), detail?.operations?.length],
    ['events', t('sandbox.log.tab.events'), detail?.events?.length],
    ['segments', t('sandbox.log.tab.segments'), detail?.usageSegments?.length],
    ['periods', t('sandbox.log.tab.periods'), detail?.billingPeriods?.length],
  ];
  const tabKind = {
    operations: 'operations', events: 'events', segments: 'usageSegments', periods: 'billingPeriods',
  }[tab];
  const shown = detail?.[tabKind]?.length || 0;
  const total = detail?.auditCounts?.[tabKind] ?? shown;
  return (
    <div className="sbx-log-panel">
      <div className="sbx-log-tabs">
        {tabs.map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            className={`sbx-log-tab${tab === key ? ' active' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
            {count ? <span className="sbx-log-tab-num">{count}</span> : null}
          </button>
        ))}
      </div>
      {error && <div className="sbx-log-error">{error}</div>}
      {/* 详情还没到（首次加载，或晚到的响应被丢弃）时不留空白。 */}
      {!error && !detail && <EmptyLine>{t('sandbox.log.loading')}</EmptyLine>}
      {!error && detail && (
        <>
          {tab === 'operations' && <SandboxOperationsList items={detail.operations} />}
          {tab === 'events' && <SandboxEventsTimeline items={detail.events} />}
          {tab === 'segments' && (
            <SandboxUsageSegments items={detail.usageSegments} totalCost={detail.totalCost} />
          )}
          {tab === 'periods' && (
            <SandboxBillingPeriods items={detail.billingPeriods} totalCost={detail.totalCost} />
          )}
          {total > shown && onLoadMore && (
            <button className="btn btn-sm btn-ghost sbx-load-more" type="button" disabled={loadingMore} onClick={() => onLoadMore(tabKind)}>
              {loadingMore ? t('sandbox.log.loading') : t('sandbox.log.loadAll', { shown, total })}
            </button>
          )}
        </>
      )}
    </div>
  );
};
